import {
    ResourceActivity,
    ResourceActivityNotificationPreferences,
    ResourceActivityResourceType,
} from '@/types';
import { dayjs } from '@/utilities';

export const iconStyle = { display: 'block' } as const;
export type ResourceActivityOrder = 'newest' | 'oldest';
export type ActivityExportFormat = 'csv' | 'json';
export const defaultNotificationPreferences: Required<ResourceActivityNotificationPreferences> = {
    collaboration: true,
    comments: true,
    content: false,
    exports: false,
    lifecycle: false,
    mentions: true,
};

const resourceTypeLabels: Partial<Record<ResourceActivityResourceType, string>> = {
    alerts: 'alert',
    dashboards: 'dashboard',
    folders: 'folder',
    investments: 'investment',
    lifecycles: 'lifecycle',
    listViews: 'list view',
    lists: 'list',
    memos: 'memo',
    pipelines: 'pipeline',
    reportComponents: 'report component',
    reports: 'report',
    tasks: 'task',
    teams: 'team',
};

export function actorName(activity: ResourceActivity) {
    const actor = activity.actor ?? activity.actorMember?.user;
    const fullName = `${actor?.firstName ?? ''} ${actor?.lastName ?? ''}`.trim();

    return fullName || actor?.emailAddress || 'System';
}

export function sortActivities(activities: ResourceActivity[], order: ResourceActivityOrder) {
    return [...activities].sort((first, second) => {
        const firstTime = dayjs(first.createdAt).valueOf();
        const secondTime = dayjs(second.createdAt).valueOf();

        return order === 'newest' ? secondTime - firstTime : firstTime - secondTime;
    });
}

export function upsertActivity(
    activities: ResourceActivity[],
    activity: ResourceActivity,
    order: ResourceActivityOrder,
) {
    const exists = activities.some((item) => item.id === activity.id);
    const nextActivities = exists
        ? activities.map((item) => (item.id === activity.id ? activity : item))
        : [...activities, activity];

    return sortActivities(nextActivities, order);
}

export function activityMatchesResource(
    activity: ResourceActivity,
    resourceType: ResourceActivityResourceType,
    resourceId: string,
    includeNested: boolean,
) {
    if (activity.resourceType === resourceType && activity.resourceId === resourceId) {
        return true;
    }

    return includeNested && activity.parentResourceType === resourceType && activity.parentResourceId === resourceId;
}

export function labelForResourceType(resourceType: ResourceActivityResourceType) {
    return resourceTypeLabels[resourceType] ?? 'resource';
}

export function downloadBlob(blob: Blob, fileName: string) {
    const downloadLink = document.createElement('a');

    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.setAttribute('download', fileName);
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(downloadLink.href);
}

const escapeCsvValue = (value: unknown) => {
    const stringValue =
        value instanceof Date
            ? value.toISOString()
            : typeof value === 'object' && value !== null
              ? JSON.stringify(value)
              : String(value ?? '');

    return `"${stringValue.replace(/"/g, '""')}"`;
};

export function activityToCsv(exportedActivities: ResourceActivity[]) {
    const columns = [
        'id',
        'createdAt',
        'eventType',
        'action',
        'title',
        'summary',
        'resourceType',
        'resourceId',
        'parentResourceType',
        'parentResourceId',
        'actor',
        'metadata',
        'changes',
    ];
    const rows = exportedActivities.map((activity) =>
        [
            activity.id,
            activity.createdAt,
            activity.eventType,
            activity.action,
            activity.title,
            activity.summary,
            activity.resourceType,
            activity.resourceId,
            activity.parentResourceType,
            activity.parentResourceId,
            actorName(activity),
            activity.metadata,
            activity.changes,
        ].map(escapeCsvValue),
    );

    return [columns.map(escapeCsvValue), ...rows].map((row) => row.join(',')).join('\n');
}
